// PAGE_PRELOAD integration — loads one page, the row's `url`, out of sight
// partway through the funnel, so that the browser already holds that page's
// files (scripts, styles, fonts, images) when the visitor is sent to a page
// like it after the pipe.
//
// When: on the first page shown that is not a landing page, not the page right
// after one, not the visit's first or second page, and carries none of the
// NO_PRELOAD_FUNNEL_MARKERS. Once per document, after the page has loaded and
// the browser is idle; built pages only, and never for a browser set to save
// data.
//
// Invariants:
//
//   * Only a page on the pipe's own site gains anything. Browsers keep a
//     separate HTTP cache per top-level site, so what a frame on the pipe's
//     domain downloads from another site is not reused when the tab later
//     opens that site. Same site = same registrable domain: `get.brand.com`
//     and `app.brand.com` share one, `brand.com` and `brand-app.com` do not.
//   * The frame is rendered at the visitor's viewport size and only made
//     transparent: with display:none it fetches no fonts and no CSS background
//     images, and a frame smaller than the viewport picks the images (srcset,
//     media queries) of a smaller screen than the one the page will open on.
//   * `sandbox` without allow-top-navigation, so a page that breaks out of
//     frames cannot take the visitor out of the funnel. It keeps
//     allow-same-origin: a frame with an opaque origin gets an HTTP cache of
//     its own, which would defeat the point.
//   * It never competes with picking a plan or paying: an unfinished preload is
//     dropped when a PAYMENT_FUNNEL_MARKERS page shows, and starts again on the
//     next page that may start one.

import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type {
  NavigationEventPayload,
  PipeDisplayMode,
  PipeInterface,
} from '@render/composables/usePipe'
import type { Page } from '@api/types.gen'

/** The row's config, validated by `PagePreloadIntegrationConfigDto` on the API. */
export interface PagePreloadConfig {
  url?: string
  /** How long the frame stays once it has loaded; DEFAULT_KEEP_AFTER_LOAD_MS when absent. */
  keepAfterLoadMs?: number
}

/**
 * Funnel markers whose pages never start the preload. Keys as in the admin's
 * funnel-marker list (renaming one there stops it matching here).
 */
export const NO_PRELOAD_FUNNEL_MARKERS: ReadonlySet<string> = new Set([
  'Landing',
  'Post Landing',
  'Plan Selection',
  'Origin Order Checkout',
])

/** Of those, the pages an unfinished preload gives way to. */
export const PAYMENT_FUNNEL_MARKERS: ReadonlySet<string> = new Set([
  'Plan Selection',
  'Origin Order Checkout',
])

/**
 * How long the frame stays once it has loaded, for what the page's scripts
 * fetch after that, unless the row sets `keepAfterLoadMs`. Removing it then
 * frees the memory and CPU a second app running in the tab costs the visitor.
 */
export const DEFAULT_KEEP_AFTER_LOAD_MS = 120_000

/** The most `keepAfterLoadMs` may be: the `@Max` of `PagePreloadIntegrationConfigDto`. */
export const KEEP_AFTER_LOAD_MAX_MS = 3_600_000

/** The longest the start waits for an idle moment once the page has loaded. */
const IDLE_TIMEOUT_MS = 3_000
/** Where there is no requestIdleCallback (older Safari). */
const IDLE_FALLBACK_MS = 1_000

const STATIC_MODE = 'static' as PipeDisplayMode.STATIC

const VISITED_PAGE_IDS_KEY = 'var.global.visitedPageIds'

type PreloadPage = Pick<Page, 'id' | 'isLanding' | 'funnelMarkers'>

/* -------------------------------------------------------------------------- */
/* Pure rules                                                                  */
/* -------------------------------------------------------------------------- */

/** The configured page as an absolute http(s) address; undefined loads nothing. */
export function resolvePreloadUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined
  } catch {
    return undefined
  }
}

/** The configured keep time, or the default for an absent or unusable one. */
export function resolveKeepAfterLoadMs(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.min(value, KEEP_AFTER_LOAD_MAX_MS)
    : DEFAULT_KEEP_AFTER_LOAD_MS
}

const hasMarker = (page: PreloadPage, markers: ReadonlySet<string>): boolean =>
  page.funnelMarkers?.some((key) => markers.has(key)) ?? false

export const isPaymentPage = (page: PreloadPage): boolean => hasMarker(page, PAYMENT_FUNNEL_MARKERS)

/**
 * Whether the preload may start on `page`. `visitedPageIds` is the visit so
 * far, `page` included: `global.visitedPageIds`, which the distribution's own
 * landing page restarts.
 */
export function mayStartPreloadOn(
  page: PreloadPage,
  visitedPageIds: readonly unknown[],
  getPage: (id: string) => PreloadPage | null,
  landingPageId: string | undefined,
): boolean {
  const isLanding = (p: PreloadPage) => p.isLanding === true || p.id === landingPageId
  if (isLanding(page) || hasMarker(page, NO_PRELOAD_FUNNEL_MARKERS)) return false
  const position = visitedPageIds.indexOf(page.id)
  // The visit's first two pages, and a page whose place in the visit is unknown.
  if (position < 2) return false
  // The page right after a landing page that did not restart the visit.
  const previous = visitedPageIds[position - 1]
  const previousPage = typeof previous === 'string' ? getPage(previous) : null
  return !(previousPage && isLanding(previousPage))
}

/* -------------------------------------------------------------------------- */
/* The frame                                                                   */
/* -------------------------------------------------------------------------- */

/** What the preloader needs from the browser. */
export interface PreloadHost {
  /** Runs `run` once the page has loaded and the browser is idle. Returns a cancel. */
  whenIdle: (run: () => void) => () => void
  /** Puts the frame in the page, calling `onLoad` once it has loaded. Returns its removal. */
  insertFrame: (url: string, onLoad: () => void) => () => void
  /** Runs `run` after `ms`. Returns a cancel. */
  after: (ms: number, run: () => void) => () => void
}

export interface PagePreloader {
  /** Starts the preload unless it has started before. */
  start: () => void
  /**
   * Drops an unfinished preload, which a later `start` begins again. A finished
   * one only loses its frame early: everything it fetched is cached.
   */
  yieldToPayment: () => void
}

const FRAME_STYLE = [
  'position:fixed',
  'top:0',
  'left:0',
  'width:100vw',
  'height:100vh',
  'margin:0',
  'border:0',
  'opacity:0',
  'pointer-events:none',
  'z-index:-2147483647',
].join(';')

const browserHost: PreloadHost = {
  whenIdle(run) {
    let idleId: number | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS })
      } else {
        timer = setTimeout(run, IDLE_FALLBACK_MS)
      }
    }
    if (document.readyState === 'complete') schedule()
    else window.addEventListener('load', schedule, { once: true })
    return () => {
      window.removeEventListener('load', schedule)
      if (idleId !== undefined) window.cancelIdleCallback(idleId)
      clearTimeout(timer)
    }
  },
  insertFrame(url, onLoad) {
    const frame = document.createElement('iframe')
    frame.src = url
    frame.loading = 'eager'
    frame.setAttribute('sandbox', 'allow-scripts allow-same-origin')
    frame.setAttribute('aria-hidden', 'true')
    frame.tabIndex = -1
    frame.inert = true
    frame.style.cssText = FRAME_STYLE
    frame.addEventListener('load', onLoad, { once: true })
    document.body.appendChild(frame)
    return () => {
      frame.removeEventListener('load', onLoad)
      frame.remove()
    }
  },
  after(ms, run) {
    const timer = setTimeout(run, ms)
    return () => clearTimeout(timer)
  },
}

export function createPagePreloader(
  url: string,
  keepAfterLoadMs: number,
  host: PreloadHost = browserHost,
): PagePreloader {
  let state: 'idle' | 'waiting' | 'loading' | 'loaded' = 'idle'
  let cancelPending: (() => void) | undefined
  let removeFrame: (() => void) | undefined

  const drop = () => {
    cancelPending?.()
    cancelPending = undefined
    removeFrame?.()
    removeFrame = undefined
  }

  return {
    start() {
      if (state !== 'idle') return
      state = 'waiting'
      cancelPending = host.whenIdle(() => {
        cancelPending = undefined
        state = 'loading'
        removeFrame = host.insertFrame(url, () => {
          state = 'loaded'
          cancelPending = host.after(keepAfterLoadMs, drop)
        })
      })
    },
    yieldToPayment() {
      if (state === 'idle') return
      drop()
      if (state !== 'loaded') state = 'idle'
    },
  }
}

/* -------------------------------------------------------------------------- */
/* Integration                                                                 */
/* -------------------------------------------------------------------------- */

export type PreloadPipe = Pick<
  PipeInterface,
  'subscribe' | 'getPageById' | 'getData' | 'whenReady' | 'data'
>

/** Starts or drops the preload as pages show. */
export function preloadOnNavigation(pipe: PreloadPipe, preloader: PagePreloader): void {
  let shows = 0
  pipe.subscribe('navigation:show', async (payload: NavigationEventPayload) => {
    const show = ++shows
    const page = pipe.getPageById(payload.pageId)
    if (!page) return
    if (isPaymentPage(page)) {
      preloader.yieldToPayment()
      return
    }
    // The page's place in the visit is known once the stored visit is merged in.
    await pipe.whenReady().catch(() => {})
    if (show !== shows) return
    const visited = pipe.getData(VISITED_PAGE_IDS_KEY)
    const landingPageId = pipe.data.value?.latestContent?.content.landingPageId
    if (
      mayStartPreloadOn(
        page,
        Array.isArray(visited) ? visited : [],
        pipe.getPageById,
        landingPageId,
      )
    ) {
      preloader.start()
    }
  })
}

const savesData = (): boolean =>
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true

const pagePreloadIntegration: PipeIntegration = (): PipeIntegrationDef => ({
  setup(pipe: PipeInterface, config?: PagePreloadConfig) {
    if (typeof window === 'undefined') return
    // Editor and admin previews would load it for authors, once per preview.
    if (pipe.currentMode.value !== STATIC_MODE) return
    const url = resolvePreloadUrl(config?.url)
    if (!url || savesData()) return
    preloadOnNavigation(
      pipe,
      createPagePreloader(url, resolveKeepAfterLoadMs(config?.keepAfterLoadMs)),
    )
  },
})

export default pagePreloadIntegration
